import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  Timestamp,
  updateDoc,
  deleteDoc
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  IndexCheckResult,
  IndexCheckSession,
  ScheduledIndexCheck,
  OxylabsApiResponse,
  ProductIndexData,
  IndexCheckStats,
  ASINScheduleStatus
} from '@/types/amazonIndexChecker';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/config/firebase';
import {
  SheetsConfig,
  ImportResult,
  SyncResult,
  DashboardASINData,
  DashboardMetrics,
  DashboardData
} from '@/types/amazonIndexChecker';
import { pmt2Service } from '@/services/pmt2Service';

// Rate limiting configuration
const RATE_LIMIT = 1; // requests per second (conservative rate for Oxylabs API)
const RATE_LIMIT_INTERVAL = 1000; // 1 second in milliseconds

class AmazonIndexCheckerService {
  private requestQueue: Array<() => Promise<any>> = [];
  private isProcessing = false;
  private lastRequestTime = 0;
  private requestCount = 0;
  private activeSessions: Map<string, IndexCheckSession> = new Map();
  private sessionTimers: Map<string, NodeJS.Timeout> = new Map();

  // Helper function to remove undefined values from objects
  private toConsistentISOString(date: Date): string {
    // Truncates milliseconds for consistent string comparison in Firestore queries
    return date.toISOString().slice(0, 19) + 'Z';
  }

  private removeUndefinedValues(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }
    
    if (typeof obj !== 'object') {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.removeUndefinedValues(item));
    }
    
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = this.removeUndefinedValues(value);
      }
    }
    return cleaned;
  }

  // Process request queue with rate limiting
  private async processQueue() {
    if (this.isProcessing || this.requestQueue.length === 0) {
      return;
    }

    this.isProcessing = true;

    while (this.requestQueue.length > 0) {
      const now = Date.now();
      const timeSinceLastBatch = now - this.lastRequestTime;

      // Reset counter if a second has passed
      if (timeSinceLastBatch >= RATE_LIMIT_INTERVAL) {
        this.requestCount = 0;
        this.lastRequestTime = now;
      }

      // Wait if we've reached the rate limit
      if (this.requestCount >= RATE_LIMIT) {
        const waitTime = RATE_LIMIT_INTERVAL - timeSinceLastBatch;
        await new Promise(resolve => setTimeout(resolve, waitTime));
        continue;
      }

      // Process next request
      const request = this.requestQueue.shift();
      if (request) {
        this.requestCount++;
        request().catch(console.error);
      }

      // Small delay between requests to avoid bursts
      await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay between requests
    }

    this.isProcessing = false;
  }

  // Check if a term is indexed for a specific ASIN
  async checkTermIndexing(
    term: string, 
    asin: string,
    onProgress?: (result: IndexCheckResult) => void
  ): Promise<IndexCheckResult> {
    return new Promise((resolve, reject) => {
      const request = async () => {
        try {
          // Call Firebase Function that will handle Oxylabs API
          const checkIndexing = httpsCallable(functions, 'checkAmazonIndexing');
          const response = await checkIndexing({
            term,
            asin,
            domain: 'com'
            // Don't pass geoLocation - let the function handle zip code selection
          });

          const data = response.data as OxylabsApiResponse;
          
          // Parse response to determine if indexed
          let isIndexed = false;
          let position: number | undefined;
          let pageNumber: number | undefined;

          if (data.results && data.results.length > 0) {
            for (const result of data.results) {
              const organicResults = result.content?.results?.organic || [];
              const foundIndex = organicResults.findIndex(item => item.asin === asin);
              
              if (foundIndex !== -1) {
                isIndexed = true;
                position = organicResults[foundIndex].pos || foundIndex + 1;
                pageNumber = result.content.page;
                break;
              }
            }
          }

          // Create result object and clean undefined values
          const rawResult = {
            term,
            asin,
            isIndexed,
            position,
            pageNumber,
            timestamp: new Date().toISOString(),
            searchUrl: `https://www.amazon.com/s?k=${encodeURIComponent(term + ' ' + asin)}`
          };
          
          const indexResult: IndexCheckResult = this.removeUndefinedValues(rawResult);

          // Report progress
          if (onProgress) {
            onProgress(indexResult);
          }

          resolve(indexResult);
        } catch (error) {
          console.error('Error checking term indexing:', error);
          reject(error);
        }
      };

      this.requestQueue.push(request);
      this.processQueue();
    });
  }

  // Check multiple terms for indexing using SCALABLE Firebase Functions with chunking
  async checkMultipleTerms(
    terms: string[],
    asin: string,
    company: string,
    userEmail: string,
    sessionId?: string,
    onProgress?: (result: IndexCheckResult, checked: number, total: number) => void
  ): Promise<IndexCheckSession> {
    try {
      const finalSessionId = sessionId || `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      console.log(`🚀 Starting SCALABLE Firebase Functions processing for ${terms.length} terms`);
      
      // Call the SCALABLE Firebase Functions batch processor (handles chunking automatically)
      const batchIndexing = httpsCallable(functions, 'batchAmazonIndexing');
      const result = await batchIndexing({
        company,
        asin,
        terms,
        userEmail,
        sessionId: finalSessionId
      });

      const data = result.data as { sessionId: string; message: string; success: boolean };
      
      if (!data.success) {
        throw new Error('Scalable batch processing failed to start');
      }
      
      // Return a session with the sessionId we used
      const session: IndexCheckSession = {
        id: finalSessionId,
        company,
        asin,
        terms,
        startedAt: new Date().toISOString(),
        results: [],
        isScheduled: false,
        userId: userEmail,
        userEmail,
        status: 'running',
        currentTermIndex: 0
      };

      console.log(`Started scalable batch processing with sessionId: ${finalSessionId} (${terms.length} terms)`);
      return session;
    } catch (error) {
      console.error('Error starting scalable batch indexing check:', error);
      throw error;
    }
  }

  // Cloud Run batch processing (for handling stuck sessions and large batches)
  async checkMultipleTermsCloudRun(
    terms: string[],
    asin: string,
    company: string,
    userEmail: string,
    sessionId?: string,
    onProgress?: (result: IndexCheckResult, checked: number, total: number) => void
  ): Promise<IndexCheckSession> {
    try {
      const finalSessionId = sessionId || `cloudrun_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      console.log(`🚀 Starting Cloud Run batch processing for ${terms.length} terms`);
      
      // Call the Cloud Run batch processing Firebase function
      const batchIndexingCloudRun = httpsCallable(functions, 'batchAmazonIndexingCloudRun');
      const result = await batchIndexingCloudRun({
        sessionId: finalSessionId,
        company,
        asin,
        searchTerms: terms,
        userId: userEmail
      });

      const data = result.data as { sessionId: string; message: string; success: boolean };
      
      if (!data.success) {
        throw new Error('Cloud Run batch processing failed to start');
      }
      
      // Return a session with the sessionId we used
      const session: IndexCheckSession = {
        id: finalSessionId,
        company,
        asin,
        terms,
        startedAt: new Date().toISOString(),
        results: [],
        isScheduled: false,
        userId: userEmail,
        userEmail,
        status: 'running',
        currentTermIndex: 0
      };

      console.log('Started Cloud Run batch processing with sessionId:', finalSessionId);
      return session;
    } catch (error) {
      console.error('Error starting Cloud Run batch indexing check:', error);
      throw error;
    }
  }

  // Recover stuck session using Cloud Run
  async recoverStuckSessionCloudRun(
    sessionId: string,
    company: string,
    asin: string
  ): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      console.log(`🔄 Attempting Cloud Run recovery for session: ${sessionId}`);
      
      const recoverStuckSessionCloudRun = httpsCallable(functions, 'recoverStuckSessionCloudRun');
      const result = await recoverStuckSessionCloudRun({
        sessionId
      });

      const data = result.data as { sessionId: string; message: string; success: boolean };
      
      return {
        success: data.success,
        message: data.message
      };
    } catch (error) {
      console.error('Error recovering stuck session with Cloud Run:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Cloud Run recovery failed'
      };
    }
  }

  // Background session processing with sequential requests (1 per second)
  private async processSessionInBackground(
    sessionId: string,
    onProgress?: (result: IndexCheckResult, checked: number, total: number) => void
  ): Promise<void> {
    const session = this.activeSessions.get(sessionId);
    if (!session) return;

    const results: IndexCheckResult[] = [...session.results];
    const startIndex = session.currentTermIndex || 0;
    const remainingTerms = session.terms.slice(startIndex);

    try {
      let completedCount = 0;

      // Start requests 1 second apart (allowing concurrent execution)
      const requestPromises = remainingTerms.map((term, index) => {
        return new Promise<void>((resolve) => {
          // Start each request after index * 1000ms delay
          setTimeout(async () => {
            try {
              console.log(`Starting API call for term "${term}" at ${new Date().toISOString()}`);
              
              // Check if session was cancelled before starting this request
              if (!this.activeSessions.has(sessionId)) {
                resolve();
                return;
              }

              const result = await this.checkTermIndexing(term, session.asin);
              
              if (result) {
                results.push(result);
                completedCount++;

                // Report progress immediately when each result comes back
                if (onProgress) {
                  onProgress(result, startIndex + completedCount, session.terms.length);
                }

                // Update session with new result
                session.results = results.map(r => this.removeUndefinedValues(r));
                session.currentTermIndex = startIndex + completedCount;
                
                // Update Firestore with the new result immediately
                await this.updateSession(session);
                
                // Update in-memory session
                this.activeSessions.set(sessionId, session);
              }
            } catch (error) {
              console.error(`Error checking term "${term}":`, error);
            }
            resolve();
          }, index * 1000); // Start requests 1 second apart
        });
      });

      // Wait for all requests to complete
      await Promise.all(requestPromises);

      // Finalize session
      session.completedAt = new Date().toISOString();
      session.status = 'completed';
      session.results = results.map(result => this.removeUndefinedValues(result));
      await this.saveSession(session);

      // Save individual results to Firestore
      await this.saveResults(results, session.company, session.asin);
      
      // Clean up
      this.activeSessions.delete(sessionId);
      if (this.sessionTimers.has(sessionId)) {
        clearInterval(this.sessionTimers.get(sessionId)!);
        this.sessionTimers.delete(sessionId);
      }
    } catch (error) {
      console.error(`Error in background session processing:`, error);
      session.status = 'error';
      await this.updateSession(session);
      this.activeSessions.delete(sessionId);
    }
  }

  // Get active session for a company/asin combination
  async getActiveSession(company: string, asin: string): Promise<IndexCheckSession | null> {
    try {
      const sessionsRef = collection(db, `amz_index_sessions/${company}/${asin}`);
      const q = query(
        sessionsRef,
        where('status', '==', 'running'),
        orderBy('startedAt', 'desc'),
        limit(1)
      );

      const snapshot = await getDocs(q);
      if (snapshot.empty) {
        return null;
      }

      const sessionData = snapshot.docs[0].data() as IndexCheckSession;
      return sessionData;
    } catch (error) {
      console.error('Error getting active session:', error);
      return null;
    }
  }

  // Resume an existing session
  async resumeSession(
    sessionId: string,
    onProgress?: (result: IndexCheckResult, checked: number, total: number) => void
  ): Promise<void> {
    try {
      // Find session in Firestore
      const sessionsRef = collection(db, 'amz_index_sessions');
      const snapshot = await getDocs(sessionsRef);
      
      let sessionDoc: any = null;
      for (const doc of snapshot.docs) {
        const subCollections = await getDocs(collection(db, doc.ref.path));
        for (const subDoc of subCollections.docs) {
          const sessionSnapshot = await getDocs(collection(db, subDoc.ref.path));
          const foundSession = sessionSnapshot.docs.find(s => s.id === sessionId);
          if (foundSession) {
            sessionDoc = foundSession.data() as IndexCheckSession;
            break;
          }
        }
        if (sessionDoc) break;
      }

      if (!sessionDoc || sessionDoc.status !== 'running') {
        return;
      }

      // Resume processing
      this.activeSessions.set(sessionId, sessionDoc);
      await this.processSessionInBackground(sessionId, onProgress);
    } catch (error) {
      console.error('Error resuming session:', error);
    }
  }

  // Cancel an active session
  async cancelSession(sessionId: string): Promise<void> {
    this.activeSessions.delete(sessionId);
    if (this.sessionTimers.has(sessionId)) {
      clearInterval(this.sessionTimers.get(sessionId)!);
      this.sessionTimers.delete(sessionId);
    }

    // Update session status in Firestore
    try {
      // Find and update session
      const sessionsRef = collection(db, 'amz_index_sessions');
      const snapshot = await getDocs(sessionsRef);
      
      for (const doc of snapshot.docs) {
        const subCollections = await getDocs(collection(db, doc.ref.path));
        for (const subDoc of subCollections.docs) {
          const sessionSnapshot = await getDocs(collection(db, subDoc.ref.path));
          const foundSession = sessionSnapshot.docs.find(s => s.id === sessionId);
          if (foundSession) {
            await updateDoc(foundSession.ref, {
              status: 'cancelled',
              completedAt: new Date().toISOString()
            });
            return;
          }
        }
      }
    } catch (error) {
      console.error('Error cancelling session:', error);
    }
  }

  // Save indexing results to Firestore
  async saveResults(results: IndexCheckResult[], company: string, asin: string): Promise<void> {
    const batch = results.map(async (result) => {
      const docPath = `amz_index/${company}/${asin}/${result.term}`;
      const docRef = doc(db, docPath);
      
      const data = {
        ...result,
        company,
        lastChecked: Timestamp.fromDate(new Date(result.timestamp)),
        history: [result] // Keep history of checks
      };

      // Get existing document to append to history
      const existingDoc = await getDoc(docRef);
      if (existingDoc.exists()) {
        const existingData = existingDoc.data();
        const history = existingData.history || [];
        history.push(result);
        // Keep only last 10 checks in history
        data.history = history.slice(-10);
      }

      // Remove undefined values before saving
      const cleanData = this.removeUndefinedValues(data);
      return setDoc(docRef, cleanData, { merge: true });
    });

    await Promise.all(batch);
    
    // Update dashboard cache after saving results
    await this.updateDashboardCache(company, asin);
  }

  // Update dashboard cache for a specific ASIN (called after index checks complete)
  private async updateDashboardCache(company: string, asin: string): Promise<void> {
    try {
      console.log(`📊 Updating dashboard cache for ${company}/${asin}`);
      
      // Get all current results for this ASIN
      const allResults = await this.getAllTermResults(company, asin);
      
      if (allResults.length === 0) {
        console.log(`⚠️ No results found for ${company}/${asin}, skipping cache update`);
        return;
      }
      
      const indexedCount = allResults.filter(r => r.isIndexed).length;
      const totalTerms = allResults.length;
      const notIndexedCount = totalTerms - indexedCount;
      const percentageIndexed = totalTerms > 0 ? Math.round((indexedCount / totalTerms) * 100) : 0;
      
      // Find most recent update time
      const lastUpdated = new Date(Math.max(...allResults.map(r => new Date(r.timestamp).getTime())));
      
      // Check for scheduled checks
      const scheduleStatus = await this.getASINScheduleStatus(company, asin);
      
      // Create/update dashboard cache document
      const cacheDocId = `${company}_${asin}`;
      const cacheDocPath = `amz_index_dashboard/${cacheDocId}`;
      const cacheDocRef = doc(db, cacheDocPath);
      
      const cacheData = {
        company,
        asin,
        totalTerms,
        indexedCount,
        notIndexedCount,
        percentageIndexed,
        lastUpdated: Timestamp.fromDate(lastUpdated),
        hasScheduledCheck: scheduleStatus.isScheduled,
        nextCheckDate: scheduleStatus.nextCheckDate || null,
        scheduledFrequency: scheduleStatus.frequency || null,
        updatedAt: Timestamp.fromDate(new Date())
      };
      
      await setDoc(cacheDocRef, this.removeUndefinedValues(cacheData));
      console.log(`✅ Updated dashboard cache for ${company}/${asin}: ${totalTerms} terms, ${percentageIndexed}% indexed`);
      
    } catch (error) {
      console.error(`❌ Error updating dashboard cache for ${company}/${asin}:`, error);
      // Don't throw - cache update failure shouldn't break the main operation
    }
  }

  // Fast dashboard data loading using cached data (CACHE-ONLY MODE)
  async getAllASINsWithDataCached(): Promise<DashboardData> {
    try {
      console.log('🚀 Loading dashboard data from cache...');
      
      const cacheRef = collection(db, 'amz_index_dashboard');
      const cacheSnapshot = await getDocs(cacheRef);
      
      // If cache is empty, return empty results (NO FALLBACK)
      if (cacheSnapshot.empty || cacheSnapshot.size === 0) {
        console.log('⚠️ Cache is empty - no data to display');
        return {
          metrics: {
            totalTermsChecked: 0,
            totalIndexed: 0,
            totalNotIndexed: 0,
            percentageIndexed: 0,
            totalASINs: 0,
            companiesCount: 0,
            lastUpdated: new Date()
          },
          asinsByCompany: {},
          loading: false
        };
      }
      
      let totalTermsChecked = 0;
      let totalIndexed = 0;
      let totalNotIndexed = 0;
      let latestUpdateTime = new Date(0);
      
      const asinsByCompany: Record<string, DashboardASINData[]> = {};
      const companiesSet = new Set<string>();
      
      cacheSnapshot.forEach(cacheDoc => {
        const cacheData = cacheDoc.data();
        const company = cacheData.company;
        // lastUpdated is stored as string by Firebase Functions, not Timestamp
        const lastUpdated = new Date(cacheData.lastUpdated);
        
        if (!asinsByCompany[company]) {
          asinsByCompany[company] = [];
        }
        
        const asinData: DashboardASINData = {
          company,
          asin: cacheData.asin,
          termsCount: cacheData.totalTerms,
          indexedCount: cacheData.indexedCount,
          notIndexedCount: cacheData.notIndexedCount,
          percentageIndexed: cacheData.indexedPercentage || 0,
          lastUpdated,
          hasScheduledCheck: false, // Will be enhanced separately if needed
          nextScheduledCheck: undefined,
          productTitle: undefined, // Will be enhanced separately
          imageUrl: undefined
        };
        
        asinsByCompany[company].push(asinData);
        companiesSet.add(company);
        
        totalTermsChecked += cacheData.totalTerms || 0;
        totalIndexed += cacheData.indexedCount || 0;
        totalNotIndexed += cacheData.notIndexedCount || 0;
        
        if (lastUpdated > latestUpdateTime) {
          latestUpdateTime = lastUpdated;
        }
      });
      
      // Sort ASINs within each company by last updated (most recent first)
      Object.keys(asinsByCompany).forEach(company => {
        asinsByCompany[company].sort((a, b) =>
          new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
        );
      });
      
      const totalASINs = cacheSnapshot.size;
      const companiesCount = companiesSet.size;
      const percentageIndexed = totalTermsChecked > 0 ?
        Math.round((totalIndexed / totalTermsChecked) * 100) : 0;
      
      const metrics: DashboardMetrics = {
        totalTermsChecked,
        totalIndexed,
        totalNotIndexed,
        percentageIndexed,
        totalASINs,
        companiesCount,
        lastUpdated: latestUpdateTime
      };
      
      console.log(`✅ Cached dashboard loaded: ${totalASINs} ASINs, ${companiesCount} companies, ${totalTermsChecked} terms`);
      
      return {
        metrics,
        asinsByCompany,
        loading: false
      };
      
    } catch (error) {
      console.error('❌ Error loading cached dashboard data:', error);
      // Return empty results if cache fails (NO FALLBACK)
      return {
        metrics: {
          totalTermsChecked: 0,
          totalIndexed: 0,
          totalNotIndexed: 0,
          percentageIndexed: 0,
          totalASINs: 0,
          companiesCount: 0,
          lastUpdated: new Date()
        },
        asinsByCompany: {},
        loading: false,
        error: error instanceof Error ? error.message : 'Cache loading failed'
      };
    }
  }

  // Save or update a check session
  async saveSession(session: IndexCheckSession): Promise<void> {
    const docPath = `amz_index_sessions/${session.company}/${session.asin}/${session.id}`;
    const docRef = doc(db, docPath);
    
    // Remove undefined values before saving
    const cleanSession = this.removeUndefinedValues(session);
    await setDoc(docRef, cleanSession);
  }

  // Update an existing session
  async updateSession(session: IndexCheckSession): Promise<void> {
    const docPath = `amz_index_sessions/${session.company}/${session.asin}/${session.id}`;
    const docRef = doc(db, docPath);
    
    // Filter out undefined values
    const updateData: any = {
      results: session.results
    };
    
    if (session.completedAt !== undefined) {
      updateData.completedAt = session.completedAt;
    }
    
    // Clean the entire updateData object to remove any undefined values
    const cleanUpdateData = this.removeUndefinedValues(updateData);
    await updateDoc(docRef, cleanUpdateData);
  }

  // Get recent sessions for a product
  async getRecentSessions(company: string, asin: string, limitCount = 10): Promise<IndexCheckSession[]> {
    const sessionsRef = collection(db, `amz_index_sessions/${company}/${asin}`);
    const q = query(
      sessionsRef,
      orderBy('startedAt', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data() as IndexCheckSession);
  }

  // Get all term results directly from individual documents
  async getAllTermResults(company: string, asin: string): Promise<IndexCheckResult[]> {
    const termsRef = collection(db, `amz_index/${company}/${asin}`);
    const snapshot = await getDocs(termsRef);
    
    const results: IndexCheckResult[] = [];
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.term) {
        results.push({
          term: data.term,
          asin: data.asin,
          isIndexed: data.isIndexed,
          position: data.position,
          timestamp: data.timestamp || data.lastChecked?.toDate().toISOString(),
          pageNumber: data.pageNumber,
          searchUrl: data.searchUrl
        });
      }
    });

    // Sort by timestamp, newest first
    return results.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }

  // Get all terms and their indexing status for a product
  async getProductIndexData(company: string, asin: string): Promise<ProductIndexData> {
    // Use the direct method to get all term results
    const results = await this.getAllTermResults(company, asin);

    // Calculate stats
    const stats: IndexCheckStats = {
      totalChecked: results.length,
      totalIndexed: results.filter(r => r.isIndexed).length,
      totalNotIndexed: results.filter(r => !r.isIndexed).length,
      lastCheckedDate: results.length > 0
        ? new Date(Math.max(...results.map(r => new Date(r.timestamp).getTime()))).toISOString()
        : '',
      averagePosition: results
        .filter(r => r.position)
        .reduce((sum, r) => sum + (r.position || 0), 0) /
        results.filter(r => r.position).length || undefined
    };

    // Get recent sessions
    const recentChecks = await this.getRecentSessions(company, asin, 5);

    // Get scheduled checks
    const scheduledChecks = await this.getScheduledChecks(company, asin);

    return {
      asin,
      company,
      lastUpdated: stats.lastCheckedDate ? Timestamp.fromDate(new Date(stats.lastCheckedDate)) : (null as unknown as Timestamp), // Convert string to Timestamp
      stats,
      recentChecks,
      scheduledChecks
    };
  }

  // Create a scheduled check
  async createScheduledCheck(
    company: string,
    asin: string,
    terms: string[],
    frequency: 'quarterly' | 'monthly' | 'one-off',
    userEmail: string
  ): Promise<ScheduledIndexCheck> {
    const checkId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Calculate next check date
    const now = new Date();
    let nextCheckDate = new Date();
    
    if (frequency === 'quarterly') {
      nextCheckDate.setMonth(nextCheckDate.getMonth() + 3);
    } else if (frequency === 'monthly') {
      nextCheckDate.setMonth(nextCheckDate.getMonth() + 1);
    }

    const scheduledCheck: ScheduledIndexCheck = {
      id: checkId,
      company,
      asin,
      terms,
      frequency,
      lastChecked: Timestamp.fromDate(now),
      nextCheckDate: frequency === 'one-off' ? Timestamp.fromDate(now) : Timestamp.fromDate(nextCheckDate),
      enabled: frequency !== 'one-off',
      createdAt: Timestamp.fromDate(now),
      createdBy: userEmail
    };

    // Use a flatter structure that collectionGroup can find
    const docId = `${company}_${asin}_${checkId}`;
    const docPath = `amz_index_scheduled/${docId}`;
    const docRef = doc(db, docPath);
    await setDoc(docRef, scheduledCheck);

    return scheduledCheck;
  }

  // Get scheduled checks for a product
  async getScheduledChecks(company: string, asin: string): Promise<ScheduledIndexCheck[]> {
    // Query using the flatter structure
    const scheduledRef = collection(db, 'amz_index_scheduled');
    const q = query(
      scheduledRef,
      where('company', '==', company),
      where('asin', '==', asin)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data() as ScheduledIndexCheck);
  }

  // Toggle scheduled check
  async toggleScheduledCheck(company: string, asin: string, checkId: string, enabled: boolean): Promise<void> {
    // Use the flatter structure
    const docId = `${company}_${asin}_${checkId}`;
    const docPath = `amz_index_scheduled/${docId}`;
    const docRef = doc(db, docPath);
    await updateDoc(docRef, { enabled });
  }

  // Delete scheduled check
  async deleteScheduledCheck(company: string, asin: string, checkId: string): Promise<void> {
    // Use the flatter structure
    const docId = `${company}_${asin}_${checkId}`;
    const docPath = `amz_index_scheduled/${docId}`;
    const docRef = doc(db, docPath);
    await deleteDoc(docRef);
  }

  // Get ASIN schedule status (ASIN-level scheduling)
  async getASINScheduleStatus(company: string, asin: string): Promise<ASINScheduleStatus> {
    try {
      const scheduledChecks = await this.getScheduledChecks(company, asin);
      const activeCheck = scheduledChecks.find(check => check.enabled && (check.frequency === 'quarterly' || check.frequency === 'monthly'));
      
      if (activeCheck && (activeCheck.frequency === 'quarterly' || activeCheck.frequency === 'monthly')) {
        return {
          isScheduled: true,
          nextCheckDate: activeCheck.nextCheckDate,
          frequency: activeCheck.frequency,
          scheduledCheckId: activeCheck.id,
          termsCount: activeCheck.terms.length,
          lastUpdated: activeCheck.lastChecked
        };
      }
      
      return { isScheduled: false };
    } catch (error) {
      console.error('Error getting ASIN schedule status:', error);
      return { isScheduled: false };
    }
  }

  // Enable ASIN-level quarterly scheduling
  async enableASINScheduling(
    company: string,
    asin: string,
    userEmail: string
  ): Promise<ScheduledIndexCheck> {
    try {
      // First, disable any existing scheduled checks for this ASIN
      await this.disableASINScheduling(company, asin);
      
      // Get all current terms for this ASIN
      const allResults = await this.getAllTermResults(company, asin);
      const terms = [...new Set(allResults.map(r => r.term))]; // Get unique terms
      
      if (terms.length === 0) {
        throw new Error('No terms found for this ASIN. Please add terms first.');
      }
      
      // Create a new quarterly scheduled check
      const scheduledCheck = await this.createScheduledCheck(
        company,
        asin,
        terms,
        'quarterly',
        userEmail
      );
      
      return scheduledCheck;
    } catch (error) {
      console.error('Error enabling ASIN scheduling:', error);
      throw error;
    }
  }

  // Disable ASIN-level scheduling
  async disableASINScheduling(company: string, asin: string): Promise<void> {
    try {
      const scheduledChecks = await this.getScheduledChecks(company, asin);
      const activeChecks = scheduledChecks.filter(check => check.enabled);
      
      // Disable all active scheduled checks for this ASIN
      await Promise.all(
        activeChecks.map(check =>
          this.toggleScheduledCheck(company, asin, check.id, false)
        )
      );
    } catch (error) {
      console.error('Error disabling ASIN scheduling:', error);
      throw error;
    }
  }

  // Update ASIN scheduling with new terms (when terms are added/changed)
  async updateASINScheduling(
    company: string,
    asin: string,
    userEmail: string
  ): Promise<void> {
    try {
      const scheduleStatus = await this.getASINScheduleStatus(company, asin);
      
      if (scheduleStatus.isScheduled && scheduleStatus.scheduledCheckId) {
        // Get updated terms
        const allResults = await this.getAllTermResults(company, asin);
        const terms = [...new Set(allResults.map(r => r.term))];
        
        // Update the existing scheduled check with new terms using flatter structure
        const docId = `${company}_${asin}_${scheduleStatus.scheduledCheckId}`;
        const docPath = `amz_index_scheduled/${docId}`;
        const docRef = doc(db, docPath);
        
        await updateDoc(docRef, {
          terms,
          lastUpdated: Timestamp.fromDate(new Date())
        });
      }
    } catch (error) {
      console.error('Error updating ASIN scheduling:', error);
      throw error;
    }
  }

  // Check a single term and update results
  async recheckSingleTerm(
    term: string,
    asin: string,
    company: string,
    userEmail: string
  ): Promise<IndexCheckResult> {
    const result = await this.checkTermIndexing(term, asin);
    
    // Save the single result
    await this.saveResults([result], company, asin);
    
    // Create a mini-session for this single check
    const sessionId = `single_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const session: IndexCheckSession = {
      id: sessionId,
      company,
      asin,
      terms: [term],
      startedAt: new Date().toISOString(),
      completedAt: new Date().toISOString(),
      results: [this.removeUndefinedValues(result)],
      isScheduled: false,
      userId: userEmail,
      userEmail,
      status: 'completed',
      currentTermIndex: 1
    };
    
    await this.saveSession(session);
    
    return result;
  }

  // Import terms from Google Sheets (initial import)
  async importFromSheets(
    spreadsheetId: string,
    company: string,
    asin: string,
    userEmail: string
  ): Promise<ImportResult> {
    try {
      const fetchTerms = httpsCallable(functions, 'fetchGoogleSheetsTerms');
      const response = await fetchTerms({
        spreadsheetId,
        company,
        asin,
        userEmail,
        isSync: false
      });

      const importResult = response.data as ImportResult;
      
      // Don't automatically trigger index checks - let user decide when to check
      // Import completed successfully
      
      return importResult;
    } catch (error) {
      console.error('Error importing from Sheets:', error);
      return {
        terms: [],
        newTerms: 0,
        duplicates: 0,
        totalTerms: 0,
        success: false,
        tabExists: false,
        error: error instanceof Error ? error.message : 'Import failed'
      };
    }
  }

  // Sync existing Google Sheets (check for new terms)
  async syncSheets(
    company: string,
    asin: string,
    userEmail: string
  ): Promise<SyncResult> {
    try {
      // Get existing config
      const config = await this.getSheetsConfig(company, asin);
      if (!config) {
        return {
          terms: [],
          newTerms: 0,
          duplicates: 0,
          totalTerms: 0,
          success: false,
          tabExists: true,
          wasSynced: false,
          error: 'No Google Sheets configuration found. Please import first.'
        };
      }

      const fetchTerms = httpsCallable(functions, 'fetchGoogleSheetsTerms');
      const response = await fetchTerms({
        spreadsheetId: config.spreadsheetId,
        company,
        asin,
        userEmail,
        isSync: true
      });

      const syncResult = response.data as SyncResult;
      
      // Don't automatically trigger index checks - let user decide when to check
      // Sync completed successfully
      
      syncResult.wasSynced = true;
      return syncResult;
    } catch (error) {
      console.error('Error syncing Sheets:', error);
      return {
        terms: [],
        newTerms: 0,
        duplicates: 0,
        totalTerms: 0,
        success: false,
        tabExists: true,
        wasSynced: false,
        error: error instanceof Error ? error.message : 'Sync failed'
      };
    }
  }

  // Get Google Sheets configuration
  async getSheetsConfig(company: string, asin: string): Promise<SheetsConfig | null> {
    try {
      const configPath = `amz_index_sessions/${company}/${asin}/sheetsConfig`;
      const configDoc = await getDoc(doc(db, configPath));
      
      if (configDoc.exists()) {
        const data = configDoc.data();
        return {
          spreadsheetId: data.spreadsheetId,
          lastSyncedAt: data.lastSyncTime?.toDate().toISOString() || '',
          importedTerms: data.importedTerms || [],
          lastManualSync: data.lastManualSync?.toDate().toISOString(),
          syncedBy: data.syncedBy,
          totalImported: data.totalImported || 0,
          lastImportCount: data.lastImportCount || 0,
          lastAutoSync: data.lastAutoSync?.toDate().toISOString(),
          autoSyncTriggered: data.autoSyncTriggered || false
        };
      }
      return null;
    } catch (error) {
      console.error('Error getting Sheets config:', error);
      return null;
    }
  }

  // Update Google Sheets configuration
  async updateSheetsConfig(
    company: string,
    asin: string,
    spreadsheetId: string,
    userEmail: string
  ): Promise<void> {
    try {
      const configPath = `amz_index_sessions/${company}/${asin}/sheetsConfig`;
      const configRef = doc(db, configPath);
      
      // Get existing config to preserve other data
      const existingConfig = await getDoc(configRef);
      
      const updateData: any = {
        spreadsheetId,
        lastUpdated: Timestamp.fromDate(new Date()),
        updatedBy: userEmail
      };
      
      if (existingConfig.exists()) {
        // Update existing config
        await updateDoc(configRef, updateData);
      } else {
        // Create new config if it doesn't exist
        await setDoc(configRef, {
          ...updateData,
          importedTerms: [],
          totalImported: 0,
          lastImportCount: 0,
          autoSyncTriggered: false
        });
      }
    } catch (error) {
      console.error('Error updating Sheets config:', error);
      throw error;
    }
  }

  // Recovery functions for stuck batch processing
  async findStuckSessions(company: string, asin: string): Promise<{
    success: boolean;
    sessions: Array<{
      id: string;
      totalTerms: number;
      processedTerms: number;
      stuckDuration: number;
      lastProcessedTerm?: string;
    }>;
    error?: string;
  }> {
    try {
      const batchRecoveryFunction = httpsCallable(functions, 'batchRecovery');
      const result = await batchRecoveryFunction({
        action: 'findStuck',
        company,
        asin
      });
      
      const data = result.data as any;
      return {
        success: data.success,
        sessions: data.data || [],
        error: data.success ? undefined : data.message
      };
    } catch (error) {
      console.error('Error finding stuck sessions:', error);
      return {
        success: false,
        sessions: [],
        error: error instanceof Error ? error.message : 'Failed to find stuck sessions'
      };
    }
  }

  async recoverStuckSession(
    sessionId: string,
    company: string,
    asin: string,
    forceComplete: boolean = false
  ): Promise<{
    success: boolean;
    resumed?: number;
    message: string;
  }> {
    try {
      const recoverCurrentSessionFunction = httpsCallable(functions, 'recoverCurrentSession');
      const result = await recoverCurrentSessionFunction({
        sessionId,
        company,
        asin,
        forceComplete
      });
      
      const data = result.data as any;
      return {
        success: data.success,
        resumed: data.data?.resumed,
        message: data.message
      };
    } catch (error) {
      console.error('Error recovering stuck session:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Recovery failed'
      };
    }
  }

  // Dashboard data aggregation method - discover data directly from index results
  async getAllASINsWithData(): Promise<DashboardData> {
    try {
      console.log('🚀 Starting dashboard data aggregation...');
      
      const asinDataMap = new Map<string, DashboardASINData>();
      let totalTermsChecked = 0;
      let totalIndexed = 0;
      let totalNotIndexed = 0;
      let latestUpdateTime = new Date(0);
      
      // Discover company/ASIN combinations directly from amz_index collection
      console.log('🎯 Discovering ASINs from actual index data...');
      const indexRef = collection(db, 'amz_index');
      const indexSnapshot = await getDocs(indexRef);
      
      const companyAsinPairs = new Set<string>();
      
      for (const companyDoc of indexSnapshot.docs) {
        const company = companyDoc.id;
        console.log(`🏢 Checking index data for company: ${company}`);
        
        const companyIndexRef = collection(db, `amz_index/${company}`);
        const companyIndexSnapshot = await getDocs(companyIndexRef);
        
        for (const asinDoc of companyIndexSnapshot.docs) {
          const asin = asinDoc.id;
          const key = `${company}_${asin}`;
          companyAsinPairs.add(key);
          console.log(`📊 Found index data for: ${company}/${asin}`);
        }
      }
      
      console.log(`🎯 Discovered ${companyAsinPairs.size} unique company/ASIN pairs from index data`);
      
      // Now process each discovered company/ASIN pair
      for (const pairKey of companyAsinPairs) {
        const [company, asin] = pairKey.split('_');
        console.log(`🔍 Processing data for: ${company}/${asin}`);
        
        try {
          // Get all terms for this ASIN using the existing method
          const termResults = await this.getAllTermResults(company, asin);
          console.log(`📝 Found ${termResults.length} term results for ${company}/${asin}`);
          
          if (termResults.length > 0) {
            const indexedCount = termResults.filter(r => r.isIndexed).length;
            const termsCount = termResults.length;
            
            // Find most recent timestamp
            const mostRecentTimestamp = termResults.length > 0
              ? new Date(Math.max(...termResults.map(r => new Date(r.timestamp).getTime())))
              : new Date(0);
            
            totalTermsChecked += termsCount;
            totalIndexed += indexedCount;
            totalNotIndexed += (termsCount - indexedCount);
            
            if (mostRecentTimestamp > latestUpdateTime) {
              latestUpdateTime = mostRecentTimestamp;
            }
            
            console.log(`✅ Adding ASIN data: ${pairKey} with ${termsCount} terms, ${indexedCount} indexed`);
            asinDataMap.set(pairKey, {
              company,
              asin,
              termsCount,
              indexedCount,
              notIndexedCount: termsCount - indexedCount,
              percentageIndexed: termsCount > 0 ? Math.round((indexedCount / termsCount) * 100) : 0,
              lastUpdated: mostRecentTimestamp,
              hasScheduledCheck: false, // Will be updated below
              productTitle: undefined,
              imageUrl: undefined
            });
          }
        } catch (error) {
          console.warn(`⚠️ Error processing ${company}/${asin}:`, error);
          // Continue with other ASINs
        }
      }
      
      // Update scheduling information for discovered ASINs
      console.log('📋 Updating scheduling information...');
      const schedulingRef = collection(db, 'amz_index_scheduled');
      const schedulingSnapshot = await getDocs(schedulingRef);
      
      schedulingSnapshot.forEach(scheduledDoc => {
        const scheduledData = scheduledDoc.data();
        const asinKey = `${scheduledData.company}_${scheduledData.asin}`;
        
        if (asinDataMap.has(asinKey) && scheduledData.enabled) {
          const asinData = asinDataMap.get(asinKey)!;
          asinData.hasScheduledCheck = true;
          asinData.nextScheduledCheck = scheduledData.nextCheckDate?.toDate();
          asinDataMap.set(asinKey, asinData);
          console.log(`📅 Updated scheduling for: ${asinKey}`);
        }
      });
      
      // Convert map to grouped data structure
      const asinsByCompany: Record<string, DashboardASINData[]> = {};
      const companiesSet = new Set<string>();
      
      asinDataMap.forEach((asinData) => {
        if (!asinsByCompany[asinData.company]) {
          asinsByCompany[asinData.company] = [];
        }
        asinsByCompany[asinData.company].push(asinData);
        companiesSet.add(asinData.company);
      });
      
      // Sort ASINs within each company by last updated (most recent first)
      Object.keys(asinsByCompany).forEach(company => {
        asinsByCompany[company].sort((a, b) =>
          new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
        );
      });
      
      // Calculate aggregate metrics
      const totalASINs = asinDataMap.size;
      const companiesCount = companiesSet.size;
      const percentageIndexed = totalTermsChecked > 0 ?
        Math.round((totalIndexed / totalTermsChecked) * 100) : 0;
      
      const metrics: DashboardMetrics = {
        totalTermsChecked,
        totalIndexed,
        totalNotIndexed,
        percentageIndexed,
        totalASINs,
        companiesCount,
        lastUpdated: latestUpdateTime
      };
      
      console.log(`✅ Dashboard aggregation complete: ${totalASINs} ASINs, ${companiesCount} companies, ${totalTermsChecked} terms`);
      
      return {
        metrics,
        asinsByCompany,
        loading: false
      };
      
    } catch (error) {
      console.error('❌ Error aggregating dashboard data:', error);
      return {
        metrics: {
          totalTermsChecked: 0,
          totalIndexed: 0,
          totalNotIndexed: 0,
          percentageIndexed: 0,
          totalASINs: 0,
          companiesCount: 0,
          lastUpdated: new Date()
        },
        asinsByCompany: {},
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load dashboard data'
      };
    }
  }

  // Enhanced method that includes PMT2 product title lookups (using cached data)
  async getAllASINsWithDataEnhanced(): Promise<DashboardData> {
    // For maximum efficiency, just return cached data without PMT2 lookups
    // PMT2 product title lookups caused inefficient searches across all companies
    return this.getAllASINsWithDataCached();
  }

  // Backfill dashboard cache for existing ASINs (run once to populate cache)
  async backfillDashboardCache(): Promise<{
    processed: number;
    updated: number;
    errors: number;
  }> {
    try {
      console.log('🔄 Starting dashboard cache backfill...');
      
      // Get all existing company/ASIN pairs from amz_index
      const indexRef = collection(db, 'amz_index');
      const indexSnapshot = await getDocs(indexRef);
      
      const companyAsinPairs: Array<{company: string, asin: string}> = [];
      
      for (const companyDoc of indexSnapshot.docs) {
        const company = companyDoc.id;
        const companyIndexRef = collection(db, `amz_index/${company}`);
        const companyIndexSnapshot = await getDocs(companyIndexRef);
        
        for (const asinDoc of companyIndexSnapshot.docs) {
          const asin = asinDoc.id;
          companyAsinPairs.push({ company, asin });
        }
      }
      
      console.log(`📊 Found ${companyAsinPairs.length} company/ASIN pairs to backfill`);
      
      let processed = 0;
      let updated = 0;
      let errors = 0;
      
      // Process each pair
      for (const { company, asin } of companyAsinPairs) {
        try {
          console.log(`🔄 Backfilling cache for ${company}/${asin}`);
          await this.updateDashboardCache(company, asin);
          updated++;
        } catch (error) {
          console.error(`❌ Error backfilling ${company}/${asin}:`, error);
          errors++;
        }
        processed++;
        
        // Small delay to avoid overwhelming Firestore
        if (processed % 10 === 0) {
          console.log(`📈 Backfill progress: ${processed}/${companyAsinPairs.length}`);
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      console.log(`✅ Dashboard cache backfill complete: ${processed} processed, ${updated} updated, ${errors} errors`);
      
      return { processed, updated, errors };
      
    } catch (error) {
      console.error('❌ Error during dashboard cache backfill:', error);
      throw error;
    }
  }
}

export const amazonIndexCheckerService = new AmazonIndexCheckerService();